import { fillText } from 'components/invoiceImport/invoiceImportText';
import { PAYMENT_TERMS, findPaymentTerm } from 'constants/paymentTerms';
import { normalizePennylaneCategoryId } from 'helper/companyOverviewDiff';

// The select's value for "no category": a select only holds strings.
export const NO_CATEGORY = 'none';

export const categoryValueOf = (id) => {
  const normalized = normalizePennylaneCategoryId(id);
  return normalized === null ? NO_CATEGORY : String(normalized);
};

export const categoryIdOf = (value) =>
  value === NO_CATEGORY ? null : Number(value);

/**
 * The category the invoice goes by. A discovered invoice has none of its own
 * yet and takes the company's at import (api README § 4, rule 6), which is
 * what the detail shows: the modal opens on the same value.
 */
export const invoiceCategoryId = (invoice) => {
  const own = invoice?.category ?? {};
  if (
    invoice?.importStatus === 'discovered' &&
    own.source !== 'invoice' &&
    own.id == null
  ) {
    return invoice.companyDefaults?.categoryId ?? null;
  }
  return own.id ?? null;
};

/** The term the invoice goes by: its own, else the company's (at import). */
export const invoicePaymentTerm = (invoice) =>
  invoice?.paymentTerm ?? invoice?.companyDefaults?.paymentTerm ?? null;

/** The key of one of the seven terms, or "" for a term outside them. */
export const paymentTermKeyOf = (term) => findPaymentTerm(term)?.key ?? '';

/** The form's values for an invoice. */
export const initialValuesOf = (invoice) => ({
  categoryValue: categoryValueOf(invoiceCategoryId(invoice)),
  termKey: paymentTermKeyOf(invoicePaymentTerm(invoice)),
});

/**
 * The body of `PUT invoices/:number/information`: the fields that differ
 * from the invoice's, and only them. An empty object means nothing to send.
 */
export const changedInformation = (initial, values) => {
  const body = {};
  if (values.categoryValue !== initial.categoryValue) {
    body.categoryId = categoryIdOf(values.categoryValue);
  }
  if (values.termKey && values.termKey !== initial.termKey) {
    const term = PAYMENT_TERMS.find(
      (candidate) => candidate.key === values.termKey,
    );
    body.paymentTerm = { days: term.days, endOfMonth: term.endOfMonth };
  }
  return body;
};

/**
 * The category options: "no category", the Pennylane list, and any id the
 * list does not carry (the invoice's, the company's), named by its number so
 * that the select never drops the value it opens on.
 */
export const categoryOptionsOf = (categories, extraIds, text) => {
  const options = [
    { value: NO_CATEGORY, label: text('noCategory', 'Aucune catégorie') },
    ...(categories ?? []).map((category) => ({
      value: categoryValueOf(category.id),
      label: category.label,
    })),
  ];
  extraIds.forEach((id) => {
    const value = categoryValueOf(id);
    if (options.some((option) => option.value === value)) return;
    options.push({
      value,
      label: fillText(text('unknownCategory', 'Catégorie n° {id}'), { id }),
    });
  });
  return options;
};
